// src/contexts/Filter.context.jsx

'use client';

import { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useSearchParams, usePathname, useRouter } from 'next/navigation';

// Create context with default values
const FiltersContext = createContext({
  filterState: {},
  updateFilterRaw: () => {},
  getFilterAsSet: () => new Set(),
  updateFilterSet: () => {},
  toggleInFilterSet: () => {},
  isInFilterSet: () => false,
  getFilterRange: () => ({ min: '', max: '' }),
  updateFilterRange: () => {},
  getFilterValue: () => '',
  updateFilterValue: () => {},
  hasFilter: () => false,
  searchParams: new URLSearchParams()
});

export function FilterProvider({ children }) {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  // Store all filter states in a single object
  const [filterState, setFilterState] = useState({});

  // Use refs for debouncing
  const pendingUpdate = useRef(null);
  const updateTimeoutRef = useRef(null);

  // Initialize filter state from URL
  useEffect(() => {
    const initialState = {};
    for (const [key, value] of searchParams.entries()) {
      initialState[key] = value;
    }
    setFilterState(initialState);
  }, [searchParams]);

  // URL update function with debouncing
  const updateURL = useCallback(() => {
    if (pendingUpdate.current) {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }

      updateTimeoutRef.current = setTimeout(() => {
        const params = new URLSearchParams();

        Object.entries(pendingUpdate.current).forEach(([key, value]) => {
          if (value !== '' && value !== null && value !== undefined) {
            params.set(key, value);
          }
        });

        const queryString = params.toString();
        const newUrl = pathname + (queryString ? `?${queryString}` : '');

        router.push(newUrl, { scroll: false });
        pendingUpdate.current = null;
      }, 300);
    }
  }, [pathname, router]);

  // Memoized update functions
  const updateFilterRaw = useCallback((filterName, value) => {
    setFilterState(prev => {
      const newState = { ...prev };
      if (value === '' || value === null || value === undefined) {
        delete newState[filterName];
      } else {
        newState[filterName] = value;
      }
      pendingUpdate.current = newState;
      updateURL();
      return newState;
    });
  }, [updateURL]);

  // Memoized getters and setters
  const getFilterAsSet = useCallback((filterName) => {
    const value = filterState[filterName];
    return value ? new Set(value.split(',')) : new Set();
  }, [filterState]);

  const updateFilterSet = useCallback((filterName, valueSet) => {
    if (!(valueSet instanceof Set)) {
      console.error('updateFilterSet requires a Set');
      return;
    }
    const arrayValue = Array.from(valueSet);
    updateFilterRaw(filterName, arrayValue.length > 0 ? arrayValue.join(',') : '');
  }, [updateFilterRaw]);

  const toggleInFilterSet = useCallback((filterName, value) => {
    const currentSet = getFilterAsSet(filterName);
    if (currentSet.has(value)) {
      currentSet.delete(value);
    } else {
      currentSet.add(value);
    }
    updateFilterSet(filterName, currentSet);
  }, [getFilterAsSet, updateFilterSet]);

  const getFilterRange = useCallback((filterName) => {
    const value = filterState[filterName];
    if (!value) return { min: '', max: '' };

    try {
      const result = {};
      value.split(',').forEach(pair => {
        const [key, val] = pair.split(':');
        result[key] = val;
      });
      return {
        min: result.min || '',
        max: result.max || '',
        ...result
      };
    } catch (e) {
      console.error("Error parsing range filter:", e);
      return { min: '', max: '' };
    }
  }, [filterState]);

  const updateFilterRange = useCallback((filterName, { min, max, ...rest }) => {
    const parts = [];
    if (min !== '' && min !== null && min !== undefined) {
      parts.push(`min:${min}`);
    }
    if (max !== '' && max !== null && max !== undefined) {
      parts.push(`max:${max}`);
    }
    Object.entries(rest).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        parts.push(`${key}:${value}`);
      }
    });
    updateFilterRaw(filterName, parts.length > 0 ? parts.join(',') : '');
  }, [updateFilterRaw]);

  const getFilterValue = useCallback((filterName) => {
    return filterState[filterName] || '';
  }, [filterState]);

  const hasFilter = useCallback((filterName) => {
    return filterName in filterState && filterState[filterName] !== '';
  }, [filterState]);

  const isInFilterSet = useCallback((filterName, value) => {
    return getFilterAsSet(filterName).has(value);
  }, [getFilterAsSet]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    filterState,
    updateFilterRaw,
    getFilterAsSet,
    updateFilterSet,
    toggleInFilterSet,
    isInFilterSet,
    getFilterRange,
    updateFilterRange,
    getFilterValue,
    updateFilterValue: updateFilterRaw,
    hasFilter,
    searchParams
  }), [
    filterState,
    updateFilterRaw,
    getFilterAsSet,
    updateFilterSet,
    toggleInFilterSet,
    isInFilterSet,
    getFilterRange,
    updateFilterRange,
    getFilterValue,
    hasFilter,
    searchParams
  ]);

  return (
    <FiltersContext.Provider value={value}>
      {children}
    </FiltersContext.Provider>
  );
}

export const useFilters = () => useContext(FiltersContext);
